import React, { useState, useRef, useCallback, useEffect } from 'react';
import { FolderType, LinkType } from '../types';
import FolderItem from './FolderItem';
import LinkItem from './LinkItem';
import SearchBar from './SearchBar';
import TagFilter from './TagFilter';
import PreviewPanel from './PreviewPanel';
import { findFreePosition, CELL_W, CELL_H, GRID_ORIGIN_X, GRID_ORIGIN_Y } from '../utils/collision';

interface CanvasProps {
  folders: FolderType[];
  links: LinkType[];
  onFoldersChange: (folders: FolderType[]) => void;
  onLinksChange: (links: LinkType[]) => void;
  onUndo: () => void;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onDeleteFolder: (id: string) => void;
  onDeleteLink: (id: string) => void;
  onQuickAddOpen: () => void;
  onCreateFolder: () => void;
}

const Canvas: React.FC<CanvasProps> = ({
  folders,
  links,
  onFoldersChange,
  onLinksChange,
  onUndo,
  selectedId,
  onSelect,
  onDeleteFolder,
  onDeleteLink,
  onQuickAddOpen,
  onCreateFolder,
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTags, setActiveTags] = useState<string[]>([]);
  const [previewLink, setPreviewLink] = useState<LinkType | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const foldersRef = useRef(folders);
  const linksRef = useRef(links);
  foldersRef.current = folders;
  linksRef.current = links;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Listen to Ctrl+K / Cmd+K and Ctrl+F / Cmd+F
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onQuickAddOpen();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onQuickAddOpen]);

  // Combine tags from both folders and links
  const allTags = Array.from(
    new Set([...links.flatMap((l) => l.tags), ...folders.flatMap((f) => f.tags)])
  );

  const filteredFolders = folders.filter((f) => {
    if (
      searchQuery &&
      !f.title.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !f.tags.some((t) => t.toLowerCase().includes(searchQuery.toLowerCase())) &&
      !f.richTextNote.toLowerCase().includes(searchQuery.toLowerCase())
    )
      return false;
    if (activeTags.length && !activeTags.some((t) => f.tags.includes(t))) return false;
    return true;
  });

  const filteredLinks = links.filter((l) => {
    // Only show links on the canvas that are NOT inside any folder
    if (l.folderIds && l.folderIds.length > 0) return false;
    if (
      searchQuery &&
      !l.title.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !l.tags.some((t) => t.toLowerCase().includes(searchQuery.toLowerCase())) &&
      !l.url.toLowerCase().includes(searchQuery.toLowerCase())
    )
      return false;
    if (activeTags.length && !activeTags.some((t) => l.tags.includes(t))) return false;
    return true;
  });

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const data = e.dataTransfer.getData('application/json');
      if (!data) return;
      const item = JSON.parse(data);
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const latestFolders = foldersRef.current;
      const latestLinks = linksRef.current;

      if (item.type === 'folder') {
        const pos = findFreePosition(x, y, item.id, latestFolders);
        onFoldersChange(
          latestFolders.map((f) => (f.id === item.id ? { ...f, position: { x: pos.x, y: pos.y } } : f))
        );
      } else if (item.type === 'link') {
        onLinksChange(
          latestLinks.map((l) => (l.id === item.id ? { ...l, position: { x, y }, folderIds: [] } : l))
        );
      } else if (item.type === 'link-from-folder') {
        // Dragged link out of its folder onto the main canvas
        onLinksChange(
          latestLinks.map((l) =>
            l.id === item.id
              ? {
                  ...l,
                  position: { x, y },
                  folderIds: l.folderIds.filter((fId) => fId !== item.folderId),
                }
              : l
          )
        );
      }
    },
    [onFoldersChange, onLinksChange]
  );

  const handleCanvasClick = (e: React.MouseEvent) => {
    // If user clicked directly on the canvas background, deselect current selection
    if (e.target === e.currentTarget) {
      onSelect(null);
    }
  };

  if (isMobile) {
    return (
      <div className="p-4 min-h-screen bg-white" dir="rtl" onClick={handleCanvasClick}>
        <div className="flex flex-col gap-3 mb-4">
          <SearchBar query={searchQuery} onChange={setSearchQuery} />
          <TagFilter tags={allTags} activeTags={activeTags} onChange={setActiveTags} />
        </div>
        {filteredFolders.length === 0 && filteredLinks.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-gray-300 font-heebo text-gray-400 text-sm">
            הקנבס ריק ונקי. השתמשו בכפתור + בסרגל העליון כדי להתחיל!
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 mt-4">
            {filteredFolders.map((f) => (
              <FolderItem
                key={f.id}
                folder={f}
                folders={folders}
                links={links}
                onFoldersChange={onFoldersChange}
                onLinksChange={onLinksChange}
                isMobile
                selectedId={selectedId}
                onSelect={onSelect}
                onDelete={onDeleteFolder}
              />
            ))}
            {filteredLinks.map((l) => (
              <LinkItem
                key={l.id}
                link={l}
                links={links}
                onLinksChange={onLinksChange}
                onPreview={setPreviewLink}
                isMobile
                selectedId={selectedId}
                onSelect={onSelect}
                onDelete={onDeleteLink}
              />
            ))}
          </div>
        )}
        {previewLink && <PreviewPanel link={previewLink} onClose={() => setPreviewLink(null)} />}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white font-heebo" dir="rtl">
      <div className="flex items-center gap-4 p-4 border-b border-solid border-black bg-white select-none">
        <SearchBar query={searchQuery} onChange={setSearchQuery} />
        <TagFilter tags={allTags} activeTags={activeTags} onChange={setActiveTags} />
      </div>
      <div
        ref={canvasRef}
        onClick={handleCanvasClick}
        className="relative flex-1 overflow-auto select-none cursor-default"
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        style={{
          minHeight: 'calc(100vh - 160px)',
          backgroundImage:
            'linear-gradient(rgba(0,0,0,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.05) 1px, transparent 1px)',
          backgroundSize: `${CELL_W}px ${CELL_H}px`,
          backgroundPosition: `${GRID_ORIGIN_X}px ${GRID_ORIGIN_Y}px`,
        }}
      >
        {folders.length === 0 && links.length === 0 && (
          <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center select-none pointer-events-none">
            <div className="text-gray-300 text-6xl font-black mb-2 select-none font-heebo">LINK2ME</div>
            <p className="text-gray-400 font-medium font-heebo text-sm">
              הקנבס ריק לחלוטין. השתמשו בכפתור + בסרגל העליון או בקיצור Ctrl+K כדי להוסיף פריטים.
            </p>
          </div>
        )}

        {filteredFolders.map((f) => (
          <FolderItem
            key={f.id}
            folder={f}
            folders={folders}
            links={links}
            onFoldersChange={onFoldersChange}
            onLinksChange={onLinksChange}
            selectedId={selectedId}
            onSelect={onSelect}
            onDelete={onDeleteFolder}
          />
        ))}
        {filteredLinks.map((l) => (
          <LinkItem
            key={l.id}
            link={l}
            links={links}
            onLinksChange={onLinksChange}
            onPreview={setPreviewLink}
            selectedId={selectedId}
            onSelect={onSelect}
            onDelete={onDeleteLink}
          />
        ))}

        {previewLink && <PreviewPanel link={previewLink} onClose={() => setPreviewLink(null)} />}
      </div>
    </div>
  );
};

export default Canvas;